/**
 * LevelFunnelTable — подвкладка «Таблица» дашборда «Воронки уровней»
 * =====================================================================
 *
 * campaign/elite/absorption — одна группа `all`, TD — три (easy/normal/hard)
 * с двухуровневой шапкой (rowSpan/colSpan, приём из AbCompareWidget). Проценты
 * везде идут через fmtPct(num, den, showAbs) — сервер числители/знаменатели,
 * клиент проценты, отдельных toFixed('%') в этом файле быть не должно.
 */

import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import { fmtInt, fmtPct, fmtSec, fmtNum } from './format';
import { GROUP_METRIC_COLUMNS, SKIP_COLUMNS, ABSORPTION_EXTRA_COLUMNS } from './columns';
import { levelFunnel as levelFunnelSpecs } from '@ggstat/chart-specs';

const { difficulties, tableColumns } = levelFunnelSpecs;
const TD_DIFFICULTIES = [{ value: 'all', label: 'Все' }, ...difficulties.map((d) => ({ value: d.key, label: d.label }))];
const DIFF_LABEL = Object.fromEntries(difficulties.map((d) => [d.key, d.label]));

/** Ячейки одной группы по общему набору столбцов + опциональные наборы. */
function groupCells(group, base, showAbs, columns, keyPrefix) {
  return columns.map((col) => (
    <TableCell key={`${keyPrefix}-${col.key}`} className="text-right">
      {col.get(group, base, showAbs)}
    </TableCell>
  ));
}

/** Столбцы поглощения: медиана/среднее/звёзды/доля побед с бустерами. */
function absorptionCells(absorption, group, showAbs, keyPrefix) {
  if (!absorption) {
    return ABSORPTION_EXTRA_COLUMNS.map((col) => (
      <TableCell key={`${keyPrefix}-${col.key}`} className="text-right">
        —
      </TableCell>
    ));
  }
  const stars = absorption.stars || [0, 0, 0, 0];
  const starsSum = stars.reduce((a, b) => a + b, 0);
  const values = {
    medianSec: fmtSec(absorption.medianSec),
    avgSec: fmtSec(absorption.avgSec),
    starsAvg: fmtNum(absorption.starsAvg, 2),
    star0: fmtPct(stars[0], starsSum, showAbs),
    star1: fmtPct(stars[1], starsSum, showAbs),
    star2: fmtPct(stars[2], starsSum, showAbs),
    star3: fmtPct(stars[3], starsSum, showAbs),
    winsWithBoosters: fmtPct(absorption.winsWithBoosters, group?.wins, showAbs)
  };
  return ABSORPTION_EXTRA_COLUMNS.map((col) => (
    <TableCell key={`${keyPrefix}-${col.key}`} className="text-right">
      {values[col.key]}
    </TableCell>
  ));
}

/** Простая шапка: Уровень + один набор метрик (campaign/elite/absorption). */
function SimpleHeader({ mode }) {
  const columns = [
    ...GROUP_METRIC_COLUMNS,
    ...(mode === 'campaign' || mode === 'elite' ? SKIP_COLUMNS : []),
    ...(mode === 'absorption' ? ABSORPTION_EXTRA_COLUMNS : [])
  ];
  return (
    <TableRow>
      <TableHead>Уровень</TableHead>
      {columns.map((col) => (
        <TableHead key={col.key} className="text-right" title={col.hint}>
          {col.label}
        </TableHead>
      ))}
    </TableRow>
  );
}

/** Двухрядная шапка TD: сложности сверху (colSpan), метрики снизу. */
function TdHeader({ difficulty }) {
  const blocks = difficulty === 'all' ? ['easy', 'normal', 'hard'] : [difficulty];
  return (
    <>
      <TableRow>
        <TableHead rowSpan={2} className="align-bottom">Уровень</TableHead>
        <TableHead rowSpan={2} className="text-right align-bottom" title={tableColumns.tdFunnel.hint}>
          {tableColumns.tdFunnel.label}
        </TableHead>
        {blocks.map((b) => (
          <TableHead key={b} colSpan={GROUP_METRIC_COLUMNS.length} className="text-center border-l">
            {DIFF_LABEL[b]}
          </TableHead>
        ))}
        <TableHead rowSpan={2} className="text-right align-bottom border-l" title={tableColumns.tdFunnelHard.hint}>
          {tableColumns.tdFunnelHard.label}
        </TableHead>
      </TableRow>
      <TableRow>
        {blocks.map((b) =>
          GROUP_METRIC_COLUMNS.map((col, i) => (
            <TableHead
              key={`${b}-${col.key}`}
              className={`text-right ${i === 0 ? 'border-l' : ''}`}
              title={col.hint}
            >
              {col.label}
            </TableHead>
          ))
        )}
      </TableRow>
    </>
  );
}

/**
 * Строка «Инсталлы» — знаменатель воронки при галке «считать от инсталлов».
 * Колонки те же, что у уровней: 2-я — uniqueStarters, 3-я — uniqueWinners (её
 * у инсталлов нет), 4-я — funnel. Match rate считается из сырых matched/count,
 * а не из готового процента, иначе галка абсолютов показала бы «(92 / 100)»
 * вместо настоящих пар устройств.
 */
function InstallsRow({ installs, columnCount, showAbs }) {
  return (
    <TableRow className="bg-muted/30">
      <TableCell className="font-medium">Инсталлы</TableCell>
      <TableCell className="text-right">{fmtInt(installs.count)}</TableCell>
      <TableCell className="text-right text-muted-foreground">—</TableCell>
      <TableCell className="text-right" title="доля инсталлов, сматченных с регистрациями">
        {fmtPct(installs.count, installs.count, showAbs)}
        <span className="block text-xs text-muted-foreground">
          сматчено {fmtPct(installs.matched, installs.count, showAbs)}
        </span>
      </TableCell>
      {Array.from({ length: Math.max(0, columnCount - 3) }).map((_, i) => (
        <TableCell key={i} className="text-right text-muted-foreground">
          —
        </TableCell>
      ))}
    </TableRow>
  );
}

export function LevelFunnelTable({ tableData, mode, showAbs }) {
  const [tdDifficulty, setTdDifficulty] = useState('all');
  const rows = tableData?.rows || [];
  const base = tableData?.base || 0;
  const isTd = mode === 'td';

  const simpleColumnCount =
    1 +
    GROUP_METRIC_COLUMNS.length +
    (mode === 'campaign' || mode === 'elite' ? SKIP_COLUMNS.length : 0) +
    (mode === 'absorption' ? ABSORPTION_EXTRA_COLUMNS.length : 0);

  const tdBlocks = tdDifficulty === 'all' ? ['easy', 'normal', 'hard'] : [tdDifficulty];
  const tdColumnCount = 2 + tdBlocks.length * GROUP_METRIC_COLUMNS.length + 1;

  return (
    <div className="space-y-3">
      {tableData?.immature && (
        <p className="text-xs text-amber-600">
          Внимание: когорты моложе {tableData.immatureDays} дней ещё не дозрели — правая
          часть воронки занижена.
        </p>
      )}

      {isTd && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Сложность (внутри таблицы)</p>
          <ToggleGroup
            type="single"
            value={tdDifficulty}
            onValueChange={(v) => v && setTdDifficulty(v)}
          >
            {TD_DIFFICULTIES.map((d) => (
              <ToggleGroupItem key={d.value} value={d.value} aria-label={d.label}>
                {d.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {isTd ? <TdHeader difficulty={tdDifficulty} /> : <SimpleHeader mode={mode} />}
          </TableHeader>
          <TableBody>
            {tableData?.fromInstalls && tableData?.installs && (
              <InstallsRow
                installs={tableData.installs}
                columnCount={isTd ? tdColumnCount : simpleColumnCount}
                showAbs={showAbs}
              />
            )}
            {rows.map((row) => {
              if (isTd) {
                const funnelValue = fmtPct(row.uniqueStartersAny, base, showAbs);
                const hardGroup = row.groups?.hard;
                const funnelHard = fmtPct(hardGroup?.uniqueWinners, base, showAbs);
                return (
                  <TableRow key={row.level}>
                    <TableCell className="font-medium">{row.level}</TableCell>
                    <TableCell className="text-right">{funnelValue}</TableCell>
                    {tdBlocks.map((b, bi) =>
                      groupCells(
                        row.groups?.[b] || {},
                        base,
                        showAbs,
                        GROUP_METRIC_COLUMNS,
                        `${row.level}-${b}`
                      ).map((cell, ci) =>
                        ci === 0
                          ? React.cloneElement(cell, { className: `${cell.props.className} border-l` })
                          : cell
                      )
                    )}
                    <TableCell className="text-right border-l">{funnelHard}</TableCell>
                  </TableRow>
                );
              }

              const group = row.groups?.all || {};
              return (
                <TableRow key={row.level}>
                  <TableCell className="font-medium">{row.level}</TableCell>
                  {groupCells(group, base, showAbs, GROUP_METRIC_COLUMNS, `${row.level}`)}
                  {(mode === 'campaign' || mode === 'elite') &&
                    groupCells(group, base, showAbs, SKIP_COLUMNS, `${row.level}-skip`)}
                  {mode === 'absorption' &&
                    absorptionCells(row.absorption, group, showAbs, `${row.level}-abs`)}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default LevelFunnelTable;
